// frontend/src/components/member-dashboard/components/CalendarView.js
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getCalendarTimeConstraints } from "../../../utils/dateUtils";
import { getSessionDisplayName } from "../../../utils/sessionTypeUtils";
import "./CalendarView.css";

// Initialize the localizer
const localizer = momentLocalizer(moment);

/**
 * Custom event component for the calendar
 * @param {Object} props - Component props
 * @param {Object} props.event - Event object
 * @returns {JSX.Element} - Rendered component
 */
const CustomEvent = ({ event }) => {
  const startTime = moment(event.start).format("h:mm A");
  return (
    <span className="calendar-event">
      {startTime} - {event.title}
    </span>
  );
};

CustomEvent.propTypes = {
  event: PropTypes.object.isRequired,
};

/**
 * CalendarView component for displaying user bookings
 * @param {Object} props - Component props
 * @param {Array} props.bookings - Array of booking objects
 * @param {Function} props.onSelectSession - Function to call when a session is selected
 * @returns {JSX.Element} - Rendered component
 */
const CalendarView = ({ bookings, onSelectSession, displayMode = "calendar" }) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get calendar time constraints
  const { minTime, maxTime } = getCalendarTimeConstraints();

  // Map bookings to calendar events
  const events = useMemo(
    () =>
      bookings.map((booking) => {
        const start = new Date(booking.appointmentDate);
        const end = new Date(start.getTime() + booking.duration * 60000);
        const baseTitle = getSessionDisplayName(booking.sessionType);
        const suiteInfo = booking.suiteAssignment?.number
          ? ` - Suite ${booking.suiteAssignment.number}`
          : "";

        return {
          id: booking._id,
          title: baseTitle + suiteInfo,
          start,
          end,
          resource: booking.sessionType,
          status: booking.status,
        };
      }),
    [bookings]
  );

  const upcomingEvents = useMemo(() => {
    const now = Date.now();

    return [...events]
      .filter(
        (event) =>
          event.start.getTime() >= now &&
          !["cancelled", "completed", "no-show"].includes(event.status)
      )
      .sort((left, right) => left.start.getTime() - right.start.getTime());
  }, [events]);

  if (isMobile || displayMode === "list") {
    return (
      <div className="calendar-mobile-list">
        {upcomingEvents.length > 0 ? (
          upcomingEvents.map((event) => (
            <button
              type="button"
              key={event.id}
              className={`mobile-session-card mobile-session-card-${event.resource}`}
              onClick={() => onSelectSession(event.id)}
            >
              <span className="mobile-session-type">{event.title}</span>
              <strong className="mobile-session-date">
                {event.start.toLocaleDateString([], {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </strong>
              <span className="mobile-session-time">
                {event.start.toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
              <span className="mobile-session-status">
                {String(event.status || "booked").replace("-", " ")}
              </span>
            </button>
          ))
        ) : (
          <div className="calendar-mobile-empty">
            <p>No upcoming sessions booked.</p>
            <p className="calendar-mobile-empty-subtext">
              Tap Book Session above to choose your next visit.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <Calendar
        localizer={localizer}
        events={events}
        defaultView="day"
        min={minTime}
        max={maxTime}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 500 }}
        components={{ event: CustomEvent }}
        formats={{
          eventTimeRangeFormat: () => "", // Hide the default time range format
        }}
        onSelectEvent={(event) => onSelectSession(event.id)}
        eventPropGetter={(event) => ({
          className: `calendar-event-${event.resource}`,
        })}
      />
    </div>
  );
};

CalendarView.propTypes = {
  bookings: PropTypes.array.isRequired,
  onSelectSession: PropTypes.func.isRequired,
  displayMode: PropTypes.oneOf(["calendar", "list"]),
};

export default CalendarView;
